import { useEffect, useMemo, useRef, useState } from "react";
import { useAssistantSSE } from "../useAssistantSSE";
import ConsentBar from "../ConsentBar";
import MessageBubble from "../MessageBubble";
import SourceChips from "../SourceChips";
import CSAT from "../CSAT";

const IconChat = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 3C6.477 3 2 6.94 2 11.8c0 2.31 1.12 4.41 2.97 5.95-.11 1.23-.47 2.42-1.2 3.71a.7.7 0 0 0 .92 1c2.08-.76 3.66-1.64 4.88-2.7.8.2 1.65.3 2.53.3 5.523 0 10-3.94 10-8.8S17.523 3 12 3z"/>
  </svg>
);
const IconRobot = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M9 2h6v2h3a2 2 0 0 1 2 2v8a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6V6a2 2 0 0 1 2-2h3V2zm-1 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm8 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM9 15h6v2H9v-2z"/>
  </svg>
);

const IconClose = (props) => (
  <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" {...props}>
    <path d="M6 6l12 12M6 18L18 6" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconMin = (props) => (
  <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" {...props}>
    <path d="M6 12h12" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconExpand = (props) => (
  <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" {...props}>
    <path d="M8 16h8M6 12h12M8 8h8" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export default function ChatWidget() {
  const {
    messages, isStreaming, sendMessage, stop,
    consent, giveConsent, lang, setLang,
    sendCsat
  } = useAssistantSSE("auto");

  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && !minimized) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, minimized]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const txt = input.trim();
    if (!txt) return;
    setInput("");
    await sendMessage(txt);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  const visibleMessages = useMemo(() => messages, [messages]);

  if (!open) {
    return (
      <button
        aria-label="Ouvrir le chat Locapiol"
        onClick={() => { setOpen(true); setMinimized(false); }}
        className="fixed bottom-5 right-5 h-14 w-14 rounded-full bg-blue-600 text-white shadow-xl flex items-center justify-center hover:scale-105 transition"
      >
        <IconRobot className="h-7 w-7" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-full max-w-md text-gray-900">
      <div className={`rounded-2xl shadow-2xl border bg-white overflow-hidden ${minimized ? "h-12" : "h-[560px]"} flex flex-col`}>

        {/* Header */}
        <div className="px-4 py-3 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconRobot className="h-5 w-5 text-white/90" />
            <strong className="tracking-wide">Locapiol Assistant</strong>
          </div>

          <div className="flex items-center gap-2">
            {/* Lang switch */}
            <select
              className="text-xs bg-white/10 backdrop-blur px-2 py-1 rounded border border-white/20 focus:outline-none"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              title="Langue"
            >
              <option value="auto">AUTO</option>
              <option value="fr">FR</option>
              <option value="de">DE</option>
              <option value="en">EN</option>
            </select>

            {/* Minimise / Restaure */}
            <button
              onClick={() => setMinimized((v) => !v)}
              className="p-2 rounded hover:bg-white/10 active:scale-95"
              title={minimized ? "Agrandir" : "Réduire"}
            >
              {minimized ? <IconExpand className="h-5 w-5 text-white" /> : <IconMin className="h-5 w-5 text-white" />}
            </button>

            {/* close */}
            <button
              onClick={() => setOpen(false)}
              className="p-2 rounded hover:bg-white/10 active:scale-95"
              title="Fermer"
              aria-label="Fermer le chat"
            >
              <IconClose className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* messages list */}
        {!minimized && (
          <>
            <div ref={listRef} className="flex-1 overflow-auto p-3 bg-gray-50">
              {visibleMessages.length === 0 && (
                <div className="text-sm text-gray-500 px-3 py-2">
                  Hello 👋 – You can ask me Questions about Rooms.
                </div>
              )}

              {visibleMessages.map((m) => (
                <div key={m.id} className="mb-2">
                  <MessageBubble role={m.role}>{m.content}</MessageBubble>

                  {/* Show Soucres */}
                  {m.role === "assistant" && m.sources?.length > 0 && (
                    <div className="mt-1">
                      <SourceChips sources={m.sources} />
                    </div>
                  )}

                  {/* Feedback CSAT */}
                  {m.role === "assistant" && (
                    <div className="mt-1">
                      <CSAT onSubmit={(csat, note) => sendCsat({ csat, note })} />
                    </div>
                  )}
                </div>
              ))}

              {isStreaming && (
                <div className="px-3 py-1 text-xs text-gray-500 flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                  Le robot rédige une réponse…
                  <button
                    onClick={stop}
                    className="ml-2 underline text-blue-600 hover:text-blue-700"
                  >
                    stop
                  </button>
                </div>
              )}
            </div>

            {/* Place for writting */}
            <form onSubmit={onSubmit} className="p-3 border-t bg-white flex gap-2">
              <textarea
                ref={inputRef}
                rows={1}
                onKeyDown={onKeyDown}
                className="flex-1 border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                placeholder={consent ? "Écrivez votre message… (Entrée pour envoyer, Shift+Entrée = ligne)" : "Veuillez accepter pour discuter"}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={!consent}
              />
              <button
                className="px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 active:scale-95 disabled:opacity-50"
                disabled={!input.trim()}
              >
                Envoyer
              </button>
            </form>
          </>
        )}

        {/* Consentement */}
        <ConsentBar
          visible={consent !== true}
          onAccept={() => giveConsent(true)}
          onReject={() => giveConsent(false)}
        />
      </div>
    </div>
  );
}
